import React, { forwardRef, useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { QRCodeSVG } from 'qrcode.react';
import { generateQRUrl } from '../config/qrConfig';
import './ReceivingLabelPrint.css';

/**
 * Receiving Label Print Component - 4x3 inch format
 * Compact layout with:
 * - Item ID with barcode
 * - SKU and description
 * - Vendor
 * - QR code for mobile access
 */
const ReceivingLabelPrint = forwardRef(({ item, poNumber, receivedDate, quantity }, ref) => {
  if (!item) {
    console.error('ReceivingLabelPrint: No item provided');
    return null;
  }

  // Format received date and time
  const dateObj = receivedDate ? new Date(receivedDate) : new Date();
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit'
  });
  const formattedTime = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // Get item data from various possible sources
  const itemSku = item.sku || item.item_sku || item.poLine?.item_sku || 'NO-SKU';
  const itemId = item.item_id || item.poLine?.item_id || item.id || itemSku;
  const itemDescription = item.poLine?.item_description || item.display_name || item.description || item.item_description || '';
  const vendorName = item.poLine?.vendor_name || item.vendor_name || '';

  // Generate QR code URL for mobile operations
  const qrUrl = generateQRUrl(itemSku);

  const labels = [];
  for (let i = 0; i < quantity; i++) {
    const labelNumber = `${i + 1}/${quantity}`;
    labels.push(
      <div key={i} className="receiving-label">
        <div className="receiving-label-content">
          {/* Header row */}
          <div className="label-header">
            <div className="label-date">{formattedDate} {formattedTime}</div>
            <div className="label-number">{labelNumber}</div>
          </div>

          {/* Main content: text left, QR right */}
          <div className="label-main">
            {/* Left column - stacked info */}
            <div className="label-sections">
              {/* Item ID with barcode */}
              <div className="label-section">
                <div className="label-title">Item ID</div>
                <div className="label-text label-text-large">{itemId}</div>
                <div className="label-barcode">
                  <BarcodeImage value={itemId} />
                </div>
              </div>

              {/* SKU */}
              <div className="label-section">
                <div className="label-title">SKU</div>
                <div className="label-text label-text-wrap">{itemSku}</div>
              </div>

              {/* Description (if different from SKU) */}
              {itemDescription && itemDescription !== itemSku && (
                <div className="label-section">
                  <div className="label-title">Description</div>
                  <div className="label-text label-text-wrap">{itemDescription}</div>
                </div>
              )}

              {/* Vendor */}
              {vendorName && (
                <div className="label-section">
                  <div className="label-title">Vendor</div>
                  <div className="label-text">{vendorName}</div>
                </div>
              )}
            </div>

            {/* QR code on right */}
            <div className="label-qr-section">
              <QRCodeSVG
                value={qrUrl}
                size={170}
                level="M"
                includeMargin={false}
              />
              <div className="label-qr-text">SCAN FOR DETAILS</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="receiving-print-container">
      {labels}
    </div>
  );
});

function BarcodeImage({ value }) {
  const svgRef = useRef(null);

  useEffect(() => {
    if (svgRef.current && value) {
      try {
        const cleanValue = value.replace(/[^\x20-\x7E]/g, '');
        JsBarcode(svgRef.current, cleanValue, {
          format: 'CODE128',
          width: 1.5,
          height: 35,
          displayValue: false,
          margin: 0
        });
      } catch (error) {
        console.error('Barcode generation failed for value:', value, error);
      }
    }
  }, [value]);

  return <svg ref={svgRef}></svg>;
}

export default ReceivingLabelPrint;
